import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { APP_CONFIG } from '@application/config';
import { ResumeResponse } from '../entities/academy-responses.model';

/**
 * HTTP client for the LiveAttendances area custom endpoints (`/LiveAttendances/*`).
 * Source of truth: `docs/academy-api-integration.md` §7.3. PascalCase, enums by name.
 * Every endpoint is 🔑 token-derived: identity comes from the token, so the viewer's
 * `PersonId` is NEVER sent in the body or query.
 */
@Injectable({ providedIn: 'root' })
export class LiveAttendanceService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(APP_CONFIG).apiUrl.academy_service;

  /**
   * `POST /LiveAttendances/{liveEventId}/vod/join` — opens a VOD watch session.
   * 🔑 identity from token: sends an empty body, never a `PersonId`.
   */
  public vodJoin(liveEventId: string): Observable<void> {
    return this.http.post<void>(
      `${this.baseUrl}/LiveAttendances/${encodeURIComponent(liveEventId)}/vod/join`,
      {},
      { withCredentials: true },
    );
  }

  /**
   * `POST /LiveAttendances/{liveEventId}/vod/progress` — reports watched seconds for
   * the open VOD session. `watchedSeconds` is a required integer query param.
   * 🔑 identity from token: sends an empty body, never a `PersonId`.
   */
  public vodProgress(liveEventId: string, positionSeconds: number): Observable<void> {
    const params = new HttpParams().set('positionSeconds', String(positionSeconds));
    return this.http.post<void>(
      `${this.baseUrl}/LiveAttendances/${encodeURIComponent(liveEventId)}/vod/progress`,
      {},
      { params, withCredentials: true },
    );
  }

  /**
   * `GET /LiveAttendances/{liveEventId}/resume` — the viewer's resume position.
   * 🔑 identity from token: never sends a `PersonId`.
   */
  public resume(liveEventId: string): Observable<ResumeResponse> {
    return this.http.get<ResumeResponse>(
      `${this.baseUrl}/LiveAttendances/${encodeURIComponent(liveEventId)}/resume`,
      { withCredentials: true },
    );
  }
}
